import React, { useState } from "react";
import { PasswordInput, PasswordStrength } from "../components/PasswordField";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Flame, Lock, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    if (form.password !== form.confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.password });
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all pl-11 pr-4 py-3 text-base";

  return (
    <div className="min-h-[100dvh] grid lg:grid-cols-2" style={{ background: "var(--sj-bg)" }}>
      <div className="relative hidden lg:block" style={{ background: "linear-gradient(140deg, #063B30, #0B5E4E)" }}>
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
              <Flame className="h-6 w-6" />
            </div>
            <span className="font-head font-extrabold text-lg">Streams of Joy</span>
          </div>
          <div>
            <div className="text-white/80 text-sm font-semibold uppercase tracking-[0.3em] mb-3">El Roi</div>
            <h1 className="font-head text-4xl xl:text-5xl font-extrabold leading-tight tracking-tight">The God That Sees Me</h1>
            <p className="mt-5 text-white/90 text-lg max-w-md font-semibold">What God cannot do does not exist!</p>
          </div>
          <p className="text-white/70 text-sm">Streams of Joy Erith London · Church Management System</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-5 py-8 sm:p-10">
        <div className="w-full max-w-md sj-fade-up">
          <Link to="/login" data-testid="back-to-login-link" className="inline-flex items-center gap-2 text-sm font-semibold mb-8 hover:underline" style={{ color: "var(--sj-primary)" }}>
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>

          {!token ? (
            <div data-testid="reset-invalid-token">
              <h2 className="font-head text-3xl font-extrabold tracking-tight" style={{ color: "var(--sj-text)" }}>Invalid link</h2>
              <p className="mt-3" style={{ color: "var(--sj-text-2)" }}>
                This reset link is missing or invalid. Please request a new one.
              </p>
              <Link to="/forgot-password" className="mt-8 inline-block w-full text-center rounded-full py-3 font-semibold text-white shadow-sm transition-all hover:brightness-105" style={{ background: "var(--sj-primary)" }}>
                Request a new link
              </Link>
            </div>
          ) : (
            <>
              <h2 className="font-head text-3xl font-extrabold tracking-tight" style={{ color: "var(--sj-text)" }}>Set a new password</h2>
              <p className="mt-2 mb-8" style={{ color: "var(--sj-text-2)" }}>
                Choose a strong password you'll remember. Minimum 6 characters.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div className="relative">
                  <Lock className="absolute z-10 left-3.5 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: "var(--sj-text-3)" }} />
                  <PasswordInput testid="reset-password-input" required autoComplete="new-password" placeholder="New password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
                </div>
                <PasswordStrength value={form.password} testid="reset-strength" />
                <div className="relative">
                  <Lock className="absolute z-10 left-3.5 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: "var(--sj-text-3)" }} />
                  <PasswordInput testid="reset-confirm-input" required autoComplete="new-password" placeholder="Confirm new password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} />
                </div>
                <button data-testid="reset-submit-button" disabled={loading} className="w-full rounded-full py-3 font-semibold text-white shadow-sm transition-all hover:brightness-105 disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>
                  {loading ? "Updating..." : "Update password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
